/**
 * 再現実行の判定（labs.md「コードで同じ状態を再現」、components.md §3.2 段階 3）。
 * sandboxd の応答から `@@viz` を読み、期待フレームと 1 枚ずつ比べる。通信はしない。
 */
import {
  compareFrames,
  deriveFrames,
  filterCompileOutput,
  parseVizStderr,
  type ExpectedFrame,
  type VizFrame,
} from '../viz';
import type { RunResponse, SandboxErrorKind } from '../sandboxd';

export type ReplayVerdict = 'AC' | 'WA' | 'RE' | 'TLE' | 'CE';

/** observability.md の `class.run.outcome` のうち、sandboxd が応答したときの値。 */
export type ReplayOutcome = 'completed' | 'compile_error' | 'runtime_error' | 'timeout';

export interface ReplayResult {
  verdict: ReplayVerdict;
  outcome: ReplayOutcome;
  /** `@@viz` から作ったフレーム（RE・TLE でも途中までは届く）。 */
  frames: VizFrame[];
  /** 期待フレーム i と届いたフレーム i が一致したか。 */
  matches: boolean[];
  /** 最初に食い違った添字（一致すれば null）。 */
  firstMismatch: number | null;
  stdout: string;
  /** `@@viz` 行を除いた stderr。 */
  stderr: string;
  /** CE のときの診断（補助の中の warning は除いたもの）。 */
  compileOutput: string;
  /** CE のときの最初のエラーの行（ユーザーコードの中だけ）。 */
  errorLine: number | null;
  truncated: boolean;
  /** 結果の 1〜2 文（copy-guidelines.md §4）。 */
  message: string;
}

const ERROR_LINE = /^main\.swift:(\d+):\d+: error:/m;
const FATAL = /Fatal error[^\n]*/;

function frameMatches(actual: VizFrame[], expected: ExpectedFrame[]): boolean[] {
  return expected.map((e, i) => {
    const a = actual[i];
    return a !== undefined && compareFrames([a], [{ state: e.state }]) === null;
  });
}

/**
 * 1 ケースの応答を判定する。userLineCount は attachVizHelper の戻り値。
 * stdins は 1 件だけ送る前提（runs[0] を見る）。
 */
export function judgeReplay(
  res: RunResponse,
  userLineCount: number,
  expected: ExpectedFrame[],
): ReplayResult {
  const base = {
    frames: [] as VizFrame[],
    matches: expected.map(() => false),
    firstMismatch: expected.length > 0 ? 0 : null,
    stdout: '',
    stderr: '',
    compileOutput: '',
    errorLine: null as number | null,
    truncated: false,
  };
  const run = res.runs?.[0];
  if (res.compile?.ok !== true || !run) {
    const { output } = filterCompileOutput(res.compile?.output ?? res.error ?? '', userLineCount);
    const m = ERROR_LINE.exec(output);
    const line = m ? Number(m[1]) : null;
    const errorLine = line !== null && line <= userLineCount ? line : null;
    return {
      ...base,
      verdict: 'CE',
      outcome: 'compile_error',
      compileOutput: output,
      errorLine,
      message:
        errorLine === null
          ? 'コンパイルできませんでした。'
          : `コンパイルできませんでした（${errorLine} 行目）。`,
    };
  }

  const trace = parseVizStderr(run.stderr);
  const frames = deriveFrames(trace);
  const matches = frameMatches(frames, expected);
  const firstMismatch = compareFrames(frames, expected);
  const common = {
    frames,
    matches,
    firstMismatch,
    stdout: run.stdout,
    stderr: trace.userStderr,
    compileOutput: '',
    errorLine: null,
    truncated: trace.truncated,
  };

  if (run.timedOut) {
    return {
      ...common,
      verdict: 'TLE',
      outcome: 'timeout',
      message: '3 秒以内に終わりませんでした。届いたところまでの盤面を表示します。',
    };
  }
  if (run.exitCode !== 0) {
    const fatal = FATAL.exec(trace.userStderr)?.[0];
    return {
      ...common,
      verdict: 'RE',
      outcome: 'runtime_error',
      message: fatal
        ? `実行中に止まりました。\`${fatal}\` が出ています。`
        : '実行中に止まりました。届いたところまでの盤面を表示します。',
    };
  }
  if (firstMismatch === null) {
    return {
      ...common,
      verdict: 'AC',
      outcome: 'completed',
      message: `${expected.length} ステップすべてで盤面が一致しました。`,
    };
  }
  let message: string;
  if (frames.length < expected.length && matches.slice(0, frames.length).every(Boolean)) {
    message = `盤面が ${frames.length} ステップ分しか届きませんでした（記録は ${expected.length} ステップ）。viz. の行を確かめられます。`;
  } else if (frames.length > expected.length && matches.every(Boolean)) {
    message = `記録より多い ${frames.length} ステップの盤面が届きました（記録は ${expected.length} ステップ）。`;
  } else {
    message = `${firstMismatch + 1} ステップ目の盤面が記録と一致しませんでした。並べて表示します。`;
  }
  return { ...common, verdict: 'WA', outcome: 'completed', message };
}

/** sandboxd が判定を返せなかったときの outcome（observability.md §6.1）。 */
export type ReplayFailureOutcome = 'busy' | 'gateway_timeout' | 'network_error' | 'http_error';

export function failureOutcome(kind: SandboxErrorKind): ReplayFailureOutcome {
  switch (kind) {
    case 'busy':
    case 'local-busy':
      return 'busy';
    case 'timeout':
      return 'gateway_timeout';
    case 'network':
      return 'network_error';
    default:
      return 'http_error';
  }
}
